import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { App } from "@/lib/capacitor/app";
import { logger } from "@/logger";
import { getLocalDevUrlScheme } from "@/utils/auth";

export function AppUrlListener() {
  const history = useHistory();
  const [isRouterReady, setIsRouterReady] = useState(false);
  const listenerHandleRef = useRef<{ remove: () => void } | null>(null);
  const pendingUrlRef = useRef<string | null>(null);

  // Mark router as ready after initial render
  useEffect(() => {
    setIsRouterReady(true);
  }, []);

  useEffect(() => {
    // Process initial launch URL (cold start)
    async function handleLaunchUrl() {
      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        logger.info({ launchUrl: launchUrl.url }, "Handling launch URL");
        processUrl(launchUrl.url);
      }
    }

    function processUrl(urlString: string) {
      try {
        const url = new URL(urlString);
        let path = url.pathname;
        const searchParams = url.search;
        const hash = url.hash;

        // Handle custom URL schemes for local development (e.g., boomer://accept-invitation/TOKEN, boomer-jongensvandebar://accept-invitation/TOKEN)
        // When using custom schemes, the URL parser treats the first segment as hostname:
        // - boomer://accept-invitation/TOKEN parses as hostname="accept-invitation", pathname="/TOKEN"
        // We need to reconstruct it as pathname="/auth/accept-invitation/TOKEN"
        const urlScheme = getLocalDevUrlScheme();
        if (url.protocol.startsWith(urlScheme) && url.hostname && url.hostname !== "localhost") {
          // Map hostname to auth path for accept-invitation
          if (url.hostname === "accept-invitation") {
            path = "/auth/accept-invitation" + path;
          } else {
            path = "/" + url.hostname + path;
          }
          logger.info(
            {
              originalUrl: urlString,
              reconstructedPath: path,
              hostname: url.hostname,
              originalPathname: url.pathname,
            },
            "Reconstructed path from custom URL scheme (local dev)",
          );
        }

        // Use regex to extract path from deeplink URLs (production web redirects)
        // Pattern: /deeplink/{app-id}/{actual-path}
        // Example: https://api.heyboomer.nl/deeplink/boomer/accept-invitation/TOKEN
        const deeplinkMatch = path.match(/^\/deeplink\/[^/]+\/(.*)$/);
        if (deeplinkMatch) {
          const extractedPath = deeplinkMatch[1];
          // Map accept-invitation to auth path to match route structure
          if (extractedPath.startsWith("accept-invitation/")) {
            path = "/auth/" + extractedPath;
          } else {
            path = "/" + extractedPath;
          }
          logger.info(
            {
              originalUrl: urlString,
              originalPath: url.pathname,
              extractedPath: path,
            },
            "Extracted path from production deeplink URL",
          );
        }

        // If router not ready, queue the navigation
        if (!isRouterReady) {
          pendingUrlRef.current = urlString;
          logger.debug({ path }, "Router not ready, queuing navigation");
          return;
        }

        logger.debug({ path, searchParams, hash }, "Navigating to deep link");
        history.push({
          pathname: path,
          search: searchParams,
          hash: hash,
        });
      } catch (error) {
        logger.error({ error, url: urlString }, "Failed to process URL");
      }
    }

    // Handle cold start launch URL
    if (isRouterReady) {
      handleLaunchUrl();
    }

    // Setup listener for URL opens while app is running
    App.addUrlOpenListener((event) => {
      logger.debug(event, "Open App Url");
      processUrl(event.url);
    }).then((handle) => {
      listenerHandleRef.current = handle;
    });

    return () => {
      // Remove only our listener, not all listeners
      if (listenerHandleRef.current) {
        listenerHandleRef.current.remove();
      }
    };
  }, [history, isRouterReady]);

  // Process pending URL once router is ready
  useEffect(() => {
    if (isRouterReady && pendingUrlRef.current) {
      logger.info({ url: pendingUrlRef.current }, "Processing pending URL");
      const urlString = pendingUrlRef.current;
      pendingUrlRef.current = null;

      try {
        const url = new URL(urlString);
        let path = url.pathname;
        const searchParams = url.search;
        const hash = url.hash;

        // Handle custom URL schemes (same logic as processUrl)
        const urlScheme = getLocalDevUrlScheme();
        if (url.protocol.startsWith(urlScheme) && url.hostname && url.hostname !== "localhost") {
          // Map hostname to auth path for accept-invitation
          if (url.hostname === "accept-invitation") {
            path = "/auth/accept-invitation" + path;
          } else {
            path = "/" + url.hostname + path;
          }
          logger.info(
            {
              originalUrl: urlString,
              reconstructedPath: path,
            },
            "Reconstructed pending URL path from custom scheme",
          );
        }

        // Handle deeplink URLs (same logic as processUrl)
        const deeplinkMatch = path.match(/^\/deeplink\/[^/]+\/(.*)$/);
        if (deeplinkMatch) {
          const extractedPath = deeplinkMatch[1];
          // Map accept-invitation to auth path to match route structure
          if (extractedPath.startsWith("accept-invitation/")) {
            path = "/auth/" + extractedPath;
          } else {
            path = "/" + extractedPath;
          }
          logger.info(
            {
              originalUrl: urlString,
              extractedPath: path,
            },
            "Extracted pending URL path from deeplink",
          );
        }

        history.push({
          pathname: path,
          search: searchParams,
          hash: hash,
        });
      } catch (error) {
        logger.error({ error, url: urlString }, "Failed to process pending URL");
      }
    }
  }, [isRouterReady, history]);

  return null;
}
